import { ChartContainer, ChartTooltip } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import { TimePeriod } from "./TimePeriodSelector";
import { getDayXAxis, getXAxisTickFormatterForPeriod } from "./axis";
import { CHART_COLORS, CHART_CONFIG } from "./chart-theme";

interface BaseChartProps {
  data: any[];
  selectedPeriod: TimePeriod;
  currentDate: Date;
  yAxisConfig: { min: number; max: number; customInterval?: number };
  chartColor: string;
  metricName: string;
  unit: string;
}

export const BaseChart = ({
  data,
  selectedPeriod,
  currentDate,
  yAxisConfig,
  chartColor,
  metricName,
  unit,
}: BaseChartProps) => {
  const hasData = data && data.length > 0;

  const roundedYAxisMax = Math.ceil(yAxisConfig.max);
  const roundedYAxisMin = Math.floor(yAxisConfig.min);

  const calculateYAxisInterval = (min: number, max: number): number => {
    if (yAxisConfig.customInterval) {
      return yAxisConfig.customInterval;
    }

    const range = max - min;
    if (range <= 10) return 2;
    if (range <= 20) return 5;
    if (range <= 50) return 10;
    if (range <= 100) return 20;
    if (range <= 200) return 50;
    if (range <= 500) return 100;
    return 200;
  };

  const getYAxisTicks = (min: number, max: number): number[] => {
    const interval = calculateYAxisInterval(min, max);
    const ticks: number[] = [];
    const minTick = Math.floor(min);
    ticks.push(minTick);

    let nextTick = minTick + interval;
    while (nextTick < max - interval * 0.5) {
      ticks.push(nextTick);
      nextTick += interval;
    }

    if (ticks[ticks.length - 1] !== max) {
      ticks.push(max);
    }

    return ticks;
  };

  if (selectedPeriod === "day") {
    const dayXAxis = getDayXAxis(currentDate);
    const chartData = hasData
      ? data
      : dayXAxis.ticks.map((tick: number) => ({
          xValue: tick,
          value: null,
        }));

    return (
      <ChartContainer className="w-full h-full relative" config={{}}>
        <LineChart data={chartData} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            type="number"
            dataKey="xValue"
            scale="time"
            domain={dayXAxis.domain}
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            ticks={dayXAxis.ticks}
            tickFormatter={dayXAxis.tickFormatter}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[roundedYAxisMin, roundedYAxisMax]}
            interval={0}
            ticks={getYAxisTicks(roundedYAxisMin, roundedYAxisMax)}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                if (data.value !== null && data.value !== undefined) {
                  return (
                    <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        {metricName}
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {data.value} {unit}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.time}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 2 }}
          />
          {hasData && (
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={CHART_CONFIG.strokeWidth}
              dot={{
                fill: chartColor,
                strokeWidth: 0,
                r: CHART_CONFIG.dotRadius,
              }}
              activeDot={{
                r: CHART_CONFIG.activeDotRadius,
                strokeWidth: 0,
                fill: chartColor,
              }}
              connectNulls={false}
            />
          )}
        </LineChart>
      </ChartContainer>
    );
  } else {
    return (
      <ChartContainer className="w-full h-full relative" config={{}}>
        <LineChart data={data} margin={CHART_CONFIG.margin}>
          <CartesianGrid
            horizontal={true}
            vertical={false}
            stroke={CHART_COLORS.grid}
            strokeDasharray={CHART_CONFIG.gridDashArray}
          />
          <XAxis
            dataKey="categoryKey"
            axisLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
            tickLine={{ stroke: CHART_COLORS.axis, strokeWidth: 1.5 }}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            height={35}
            interval={0}
            tickFormatter={getXAxisTickFormatterForPeriod(
              selectedPeriod,
              currentDate
            )}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: CHART_COLORS.tick }}
            width={45}
            padding={{ bottom: 10 }}
            domain={[roundedYAxisMin, roundedYAxisMax]}
            interval={0}
            ticks={getYAxisTicks(roundedYAxisMin, roundedYAxisMax)}
            allowDataOverflow
          />
          <ChartTooltip
            content={({ active, payload }) => {
              if (active && payload && payload.length) {
                const data = payload[0].payload;
                if (data.value !== null) {
                  return (
                    <div className="bg-popover border border-border rounded-lg shadow-xl p-3">
                      <p className="text-sm font-medium text-muted-foreground">
                        {metricName}
                      </p>
                      <p className="text-lg font-bold text-foreground">
                        {data.value} {unit}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {data.categoryKey}
                      </p>
                    </div>
                  );
                }
              }
              return null;
            }}
            cursor={{ stroke: CHART_COLORS.axis, strokeWidth: 1 }}
          />
          {hasData && (
            <Line
              type="monotone"
              dataKey="value"
              stroke={chartColor}
              strokeWidth={CHART_CONFIG.strokeWidth}
              dot={{
                fill: chartColor,
                strokeWidth: 0,
                r: CHART_CONFIG.dotRadius,
              }}
              activeDot={{
                r: CHART_CONFIG.activeDotRadius,
                strokeWidth: 0,
                fill: chartColor,
              }}
              connectNulls={true}
            />
          )}
        </LineChart>
      </ChartContainer>
    );
  }
};
